import { Injectable } from '@angular/core';
import { DocumentData, DocumentInfo, FormDocument, FormTemplate } from '@formbird/types';
import { select} from '../../redux/decorators/select';
import { IApplicationState } from '../../redux/state/application.state';
import { Observable, Subject } from 'rxjs';
import { formResetDocumentInfo, resetUndoRedoDocument } from '../../redux/actions/form-new.actions';
import { CurrentDocumentInfoService } from './current-document-info.service';
import { DocumentService } from './document.service';
import { AppStore } from '../../redux/store/app.store';

/**
 * This service stores the document data for the document on main template.
 * It will be retrieved to navigate to document after creating from template, command bar buttons or left/right sidebar visibility...
 */

@Injectable({
  providedIn: 'root'
})
export class CurrentDocumentService {

  @select(['formState', 'documents']) documents$: Observable<any>;
  @select(['formState', 'documentInfo']) documentInfo$: Observable<any>;

  private documentData: DocumentData;
  private documentDataSubject: Subject<DocumentData>;
  public documentData$: Observable<DocumentData>;
  private mainUnsavedDocumentListId: string;

  constructor(
    private appStore: AppStore<IApplicationState>,
    private currentDocumentInfoService: CurrentDocumentInfoService,
    private documentService: DocumentService
  ) {

    this.documentData = {
      document: null,
      template: null
    };
    this.documentDataSubject = new Subject<DocumentData>();
    this.documentData$ = this.documentDataSubject.asObservable();

    this.documents$.subscribe(documents => {

      this.updateDocumentData();

    });

    this.documentInfo$.subscribe(documentInfos => {

      this.updateDocumentData();

    });
  }

  private updateDocumentData() {

    let changed = false;

    const documentInfos = this.appStore.getState().formState.documentInfo;
    if (documentInfos && documentInfos[this.mainUnsavedDocumentListId]) {
      const docInfo: DocumentInfo = documentInfos[this.mainUnsavedDocumentListId];

      this.documentData.documentId = docInfo.documentId;
      this.documentData.templateId = docInfo.templateId;
      this.documentData.options = docInfo.options;
      this.documentData.overrideTemplateId = docInfo.overrideTemplateId;
      this.documentData.showFormAfterCreated = docInfo.showFormAfterCreated;
      this.documentData.hierarchyInfo = docInfo.hierarchyInfo;
      this.documentData.altTemplateId = docInfo.altTemplateId;
      this.documentData.isNew = docInfo.isNew;
      this.documentData.isMainDoc = docInfo.isMainDoc;

      changed = true;

    }

    const documents = this.appStore.getState().formState.documents;

    if (documents && Object.keys(documents)) {
      const document = documents[this.documentData.documentId];
      if (document) {
        this.documentData.document = document;
        changed = true;
      }

      const template = documents[this.documentData.templateId];
      if (template) {
        this.documentData.template = template;
        changed = true;
      }
    }

    if (changed) {
      this.documentDataSubject.next(this.documentData);
    }

  }

  public setMainDocumentListId(documentListId) {
    this.mainUnsavedDocumentListId = documentListId;
  }

  public getDocumentData(): DocumentData {
    return this.currentDocumentInfoService.getMainDocumentData();
  }

  /**
   * get the document from the main document - which is the document that the object nav bar is linked to
   */
  public getDocument(): FormDocument {
    return this.getDocumentData().document;
  }

  public setDocument(document) {
    this.documentService.setLoadedDocumentOnly(document);
  }

  /**
   * get the template from the main document - which is the document that the object nav bar is linked to
   */
  public getTemplate(): FormTemplate {
    return this.getDocumentData().template;
  }

  public setTemplate(template) {

    this.documentService.setLoadedDocumentOnly(template);
  }

  public resetDocumentData() {

    const mainUnsavedDocListId = this.currentDocumentInfoService.getMainUnsavedDocumentListId();
    this.appStore.dispatch(formResetDocumentInfo(mainUnsavedDocListId));
  }

  public resetUndoRedo() {
    this.appStore.dispatch(resetUndoRedoDocument());
  }

}
